/* =============================================================================
 * URLs de arquivo (escudos, banners, fotos, anexos).
 *
 * As colunas do banco (Competicao.Imagem, ClubeLogo, ClubeMandanteLogo...)
 * guardam um CAMINHO DE ARQUIVO NO SERVIDOR, nao uma URL. Para exibir e'
 * preciso empacotar esse caminho junto com o identificador do tenant e
 * criptografar - o resultado vai em /file?p=<hash>&v=v2.
 *
 * QUEM DIZ ONDE FICA O /file E' O SERVIDOR, no shell: `ConfigGeral.UrlPrismaFile`
 * e' resolvido a cada requisicao (LibPrisma.ObtemUrlPrismaFile), e por isso vale
 * mais do que a `UrlServico` que o aparelho tem em maos - o ambiente pode servir
 * arquivo por outro host, e ai' a queda `<UrlServico>/file` responde 404 em toda
 * imagem do app. So' o `UrlServicoFile` do config.js do proprio servidor
 * atropela essa resposta, que e' para o que ele existe.
 *
 * Porte de LibGeral.GetUrlFile (iPrisma\scripts\LibGeral.js:3098-3138), inclusive
 * nessa ordem de precedencia.
 * ========================================================================== */

import { urlBaseArquivo } from './config.js';
import { criptografarBase64Derivado, descriptografarBase64 } from './cripto.js';
import { sessao } from '@shared/sessao/sessao.js';
import { paraInteiro, preenchido } from './valores.js';

//A chave nasce ofuscada no proprio LibGeral. Mantida assim de proposito: se
//alguem trocar a chave no backend, os dois lados continuam derivando do mesmo
//literal em vez de sair de sincronia com um texto copiado na mao.
const CHAVE_ARQUIVO = descriptografarBase64('CCoxKzUYaW5sHA==', 'X');

/**
 * `view` NAO ABRE NO NAVEGADOR - e' o que ele promete, e nao o que acontece. O
 * /file da API ignora o parametro (`FileUrl(string p, string v, bool isdebug)`,
 * em PrismaApi\Controllers\FileController.cs) e responde SEMPRE com
 * `Content-Disposition: attachment`, entao todo link para arquivo baixa. A
 * flag continua sendo enviada porque o iPrisma tambem a manda (LibGeral.js) e um
 * dia o servidor pode passar a honra-la; quem escreve o rotulo do link e' que nao
 * pode prometer "abrir". Imagem em `<img>` nao e' afetada: o `src` renderiza os
 * bytes independentemente do cabecalho.
 *
 * @param {object} args { fullName?, idArquivo?, identificadorConexao?, view? }
 * @returns {string} URL completa, ou '' quando nao ha' arquivo.
 */
export function urlArquivo(args) {
    if (!args) return '';

    const caminho = args.fullName ?? args.FullName ?? args.fileNameTemporario ?? '';
    const idArquivo = paraInteiro(args.idArquivo ?? args.IdArquivo);
    if (!preenchido(caminho) && idArquivo <= 0) return '';

    const info = {
        IdentificadorConexao: args.identificadorConexao || sessao.identificadorConexao || '',
    };
    if (preenchido(caminho)) info.FullName = caminho;
    if (idArquivo > 0) info.IdArquivo = idArquivo;

    //saltDeterministico: a mesma imagem gera sempre a mesma URL, o que e' o que
    //torna o cache do service worker util. Sem isso, cada carga baixaria de novo.
    let hash = criptografarBase64Derivado(JSON.stringify(info), CHAVE_ARQUIVO, true);
    hash = hash.replaceAll('+', '@@').replaceAll('/', '--');

    //`sessao.urlServicoArquivo` e' o `ConfigGeral.UrlPrismaFile` do ultimo shell,
    //guardado no aparelho - ver a nota dele em sessao/sessao.js.
    let url = `${urlBaseArquivo(sessao.urlServicoArquivo)}?p=${hash}&v=v2`;
    if (args.view === true) url += '&view=true';
    return url;
}

/** Atalho para logo de clube / banner de competicao vindos direto do DTO. */
export function urlImagem(caminho, opcoes = {}) {
    return preenchido(caminho) ? urlArquivo({ fullName: caminho, ...opcoes }) : '';
}

/* -----------------------------------------------------------------------------
 * Envio: do <input type="file"> para o FileBytes do backend
 * -------------------------------------------------------------------------- */

/**
 * Le a imagem escolhida e devolve o FileBytes que as funcionalidades de upload
 * esperam (ver AlterarTabUsuarioFoto -> CriarTabArquivo, no C#).
 *
 * REDIMENSIONAR NAO E' ENFEITE. A camera de um celular entrega 4 a 12 MB, o
 * base64 infla isso em mais um terco, e o /executarfuncionalidade recebe TUDO
 * num JSON so' - mandar o original cru trava o envio numa rede de estadio. O
 * iPrisma resolve o mesmo problema com a lib ImageCompressor a 2560px
 * (controls/ControlUpload.js); aqui o canvas do proprio browser basta, e para um
 * retrato 512px ja' e' o dobro do maior lugar em que ele aparece.
 *
 * SAI SEMPRE JPEG, inclusive de PNG: o formato nao tem canal alfa, entao o fundo
 * branco e' pintado ANTES - sem ele, transparencia vira preto.
 *
 * `Mime` vai como tipo puro ('image/jpeg'). O ControlUpload manda o prefixo
 * inteiro da data URL ali ('data:image/jpeg;base64,'), mas o C# nunca le esse
 * campo no caminho de gravacao - quem conta e' o Base64String, e ele vai limpo.
 */
export async function lerImagemComoFileBytes(arquivo, { ladoMaximo = 512, qualidade = 0.85 } = {}) {
    if (!arquivo || !arquivo.type?.startsWith('image/')) throw new Error('Escolha um arquivo de imagem.');

    //'from-image' respeita o EXIF: sem isso a foto tirada com o celular em pe'
    //chega deitada, que e' como a camera realmente grava os bytes.
    const bitmap = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
    const escala = Math.min(1, ladoMaximo / Math.max(bitmap.width, bitmap.height));
    const largura = Math.max(1, Math.round(bitmap.width * escala));
    const altura = Math.max(1, Math.round(bitmap.height * escala));

    const tela = document.createElement('canvas');
    tela.width = largura;
    tela.height = altura;

    const pincel = tela.getContext('2d');
    pincel.fillStyle = '#ffffff';
    pincel.fillRect(0, 0, largura, altura);
    pincel.drawImage(bitmap, 0, 0, largura, altura);
    bitmap.close();

    const dataUrl = tela.toDataURL('image/jpeg', qualidade);
    const nome = nomeSeguroDeArquivo(arquivo.name);

    return {
        Name: nome,
        Descricao: nome,
        Mime: 'image/jpeg',
        Base64String: dataUrl.slice(dataUrl.indexOf('base64,') + 'base64,'.length),
        ArquivoExistente: false,
    };
}

/**
 * `Name` vira NOME DE ARQUIVO NO DISCO DO SERVIDOR (CriarTabArquivo concatena
 * ele no diretorio de destino), entao o que sai do navegador nao pode chegar
 * cru: sobram letras, numeros, hifen e sublinhado, e a extensao e' sempre .jpg
 * porque a imagem foi reescrita como JPEG acima.
 */
function nomeSeguroDeArquivo(nome) {
    const base = String(nome ?? '')
        .replace(/\.[^.]*$/, '')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 40)
        .replace(/^-|-$/g, '');

    return `${base || 'imagem'}.jpg`;
}

/**
 * Abre um FileBytes (ex: nominata em PDF de GerarTabPartidaNominata).
 *
 * O backend pode devolver o conteudo de tres formas, dependendo da
 * funcionalidade: `Bytes` (byte[], que o Newtonsoft serializa como base64),
 * `Base64String`, ou apenas o caminho do arquivo no servidor. Tratamos as tres.
 */
export function abrirFileBytes(fileBytes) {
    if (!fileBytes) return false;

    const base64 = typeof fileBytes.Bytes === 'string' ? fileBytes.Bytes : fileBytes.Base64String;

    if (base64) {
        const binario = atob(base64);
        const bytes = new Uint8Array(binario.length);
        for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);

        const blob = new Blob([bytes], { type: fileBytes.Mime || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        //Revogar na hora cancelaria a aba que acabou de abrir.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return true;
    }

    //Sem bytes: o arquivo ficou no servidor, entao abrimos pelo /file.
    const caminho = fileBytes.FullName || fileBytes.FileNameTemporario;
    if (caminho) {
        window.open(urlArquivo({ fullName: caminho, view: true }), '_blank');
        return true;
    }
    return false;
}
